<template>
  <div>
    <!-- Language Selector -->
    <div class="form-group form-row">
      <label class="col-lg-4 col-md-6 col-form-label pt-0">{{ $t('Webportal.Administration.GuidelineEditor.Language') }}</label>
      <div class="col">
        <b-dropdown variant="outline-secondary" size="sm">
          <template #button-content>
            <font-awesome-icon v-if="selectedLocalizationId === 'deflt'" icon="wrench" fixed-width />
            <span v-else :class="['flag-icon', 'flag-icon-' + getRegion(selectedLocalizationId)]"></span>
            <span class="ml-1">{{ selectedLanguageName }}</span>
          </template>
          <b-dropdown-item
            v-for="localization in availableLocalizations"
            :key="localization.id"
            @click="selectedLocalizationId = localization.id"
          >
            <font-awesome-icon v-if="localization.id === 'deflt'" icon="wrench" fixed-width />
            <span v-else :class="['flag-icon', 'flag-icon-' + getRegion(localization.id)]"></span>
            <span class="ml-1">{{ localization.name }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Guideline Title -->
    <div class="form-group form-row">
      <label class="col-lg-4 col-md-6 col-form-label pt-0">{{ $t('Webportal.Administration.GuidelineEditor.Title') }}</label>
      <div class="col">
        <b-form-input :value="currentTitle" @input="updateTitle($event)" :disabled="disabled" :placeholder="$t('Webportal.Administration.GuidelineEditor.Title.Placeholder')" />
      </div>
    </div>

    <!-- Guideline Description -->
    <div class="form-group form-row">
      <label class="col-lg-4 col-md-6 col-form-label pt-0">{{ $t('Webportal.Administration.GuidelineEditor.Description') }}</label>
      <div class="col">
        <b-form-textarea
          :value="currentDescription"
          @input="updateDescription($event)"
          :disabled="disabled"
          :placeholder="$t('Webportal.Administration.GuidelineEditor.Description.Placeholder')"
          rows="3"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AppConfig from "@/mixins/AppConfig-APP_TARGET";

export default {
  name: "GuidelineEditor",
  mixins: [AppConfig],

  props: {
    guidelineTexts: { type: Array, default: () => [] },
    allowedLocalizations: { type: Array, default: () => ["deflt"] },
    localizationData: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      selectedLocalizationId: "deflt"
    };
  },

  computed: {
    availableLocalizations() {
      const allowed = this.allowedLocalizations;
      const result = [];

      for (const locId of allowed) {
        const group = this.localizationData.filter((e) => e.localizationTextId === locId);

        if (group.length > 0) {
          const languageName =
            group.length > 1 ? `${group[0].languageNameShort} - ${group[1].languageNameShort}` : group[0].languageNameShort;

          result.push({
            id: locId,
            name: languageName,
          });
        } else {
          // Fallback for when localization data is not loaded yet
          result.push({
            id: locId,
            name: locId === "deflt" ? this.$t('Webportal.Default') : locId,
          });
        }
      }

      return result;
    },

    selectedLanguageName() {
      const localization = this.availableLocalizations.find((l) => l.id === this.selectedLocalizationId);
      return localization?.name ?? this.selectedLocalizationId;
    },

    currentGuideline() {
      if (!this.selectedLocalizationId) return null;
      return this.guidelineTexts.find((g) => g.LocalizationId === this.selectedLocalizationId);
    },

    currentTitle() {
      return this.currentGuideline?.Title ?? "";
    },

    currentDescription() {
      return this.currentGuideline?.Description ?? "";
    },
  },

  watch: {
    allowedLocalizations() {
      // Reset selection when the current localization is no longer available for the survey
      if (!this.allowedLocalizations.includes(this.selectedLocalizationId)) {
        this.selectedLocalizationId = "deflt";
      }
    },
  },

  methods: {
    getRegion(localizationId) {
      if (!localizationId || !localizationId.includes("-")) return "";
      return localizationId.split("-")[1].toLowerCase();
    },

    findOrCreateGuideline() {
      let entry = this.guidelineTexts.find((g) => g.LocalizationId === this.selectedLocalizationId);
      if (!entry) {
        entry = {
          LocalizationId: this.selectedLocalizationId,
          Title: "",
          Description: "",
        };
        this.guidelineTexts.push(entry);
      }
      return entry;
    },

    updateTitle(value) {
      const entry = this.findOrCreateGuideline();
      entry.Title = value;
      this.$emit("change");
    },

    updateDescription(value) {
      const entry = this.findOrCreateGuideline();
      entry.Description = value;
      this.$emit("change");
    },
  },
};
</script>
